<template>
  <div class="product-set-tile">
    <a :href="bundleUrl" :aria-label="altText">
      <PreserveRatio :width="preserveWidth" :height="preserveHeight">
        <ResponsiveImage
          :url="heroImageUrl"
          :widths="responsiveImageWidths" 
          :breakpoints="responsiveImageBreakpoints"
          :skeleton="true"
          :lazy="true"
          :width="preserveWidth"
          :height="preserveHeight"
          @imageLoaded="onImgLoad"
          :alt="altText"
        />
      </PreserveRatio>
      <div class="product-set-info">
        <div class="product-set-title" :class="{ skeleton: !mounted || !set.title }">
          {{ set.title }}
        </div>
        <div class="product-set-price" :class="{ skeleton: !mounted || !prices.finalPrice }">
          <span v-if="prices.totalDiscount" class="original-total">{{ formatAndRoundDownMoney(currency, prices.originalPrice) }}</span>
          <span class="final-total bold accent">{{ formatAndRoundUpMoney(currency, prices.finalPrice) }}</span>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import { PreserveRatio } from '@/pixel/index';
import { onMounted, ref, computed } from 'vue';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import { getSessionCountry } from '@/components/commons/utils.js';
import { formatAndRoundDownMoney, formatAndRoundUpMoney } from '@/components/cart/utils.js';
import useProductBundle from '@/compositions/useProductBundle.js';
import useResize from '@/compositions/useResize';
import altTextCreator from '@/utils/accessibilityUtils.js';

export default {
  name: 'ProductSetTile',
  components: {
    ResponsiveImage,
    PreserveRatio,
  },
  props: {
    set: {
      type: Object,
      required: true,
    },
    mobileBreakpoint: {
      type: Number,
      default: 768,
    },
    primaryRoom: {
      type: String,
      default: null,
    },
    largeImages: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { getProductBundlePrice } = useProductBundle();
    const prices = getProductBundlePrice(props.set);
    const mounted = ref(false);

    onMounted(() => {
      mounted.value = true;
    });

    const { width } = useResize();

    const heroImageUrl = computed(() => {
      let key = props.set.mobileFileKey || props.set.fileKey;
      
      if (width.value > props.mobileBreakpoint) {
        key = props.set.fileKey;
      }
      if (!key) {
        key = props.set?.productBundleProducts[0]?.productThumbnails[0]?.imageUrl;
      }
      return `${key}?fit=max&w={width}&q={q}`;
    });

    const currency = computed(() => {
      const country = getSessionCountry();
      return country.currencySymbol;
    });

    const bundleUrl = computed(() => `/furniture-bundles/${props.set.id}/${props.set.slug}`);

    const preserveWidth = computed(() => (!width.value || width.value > props.mobileBreakpoint ? 2890 : 400));
    const preserveHeight = computed(() => (!width.value || width.value > props.mobileBreakpoint ? 1500 : 472));

    const responsiveImageWidths = computed(() => (props.largeImages ? [768, 356, 475, 590] : [320, 425, 768, 425]));
    const responsiveImageBreakpoints = computed(() => (props.largeImages ? [320, 768, 1200, 1600] : [320, 425, 768, 970]));
    
    const altText = computed(() => {
      const defaultAlt = props.set?.title ?? 'Bundle Image';
      const secondaryAlt = ', contains products'

      const productTitles = props.set.productBundleProducts.map((product) => product.productThumbnails[0].title);

      return altTextCreator({startingText: defaultAlt, secondaryText: secondaryAlt, productTitles: productTitles , isConcatText: true});
    });
   
    return {
      mounted,
      heroImageUrl,
      bundleUrl,
      currency,
      formatAndRoundDownMoney,
      formatAndRoundUpMoney,
      prices,
      preserveWidth,
      preserveHeight,
      responsiveImageWidths,
      responsiveImageBreakpoints,
      altText,
    };
  },
  methods: {
    onImgLoad() {
      this.$emit('imageLoaded');
    }
  }
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';
.product-set-tile {
  .product-set-info {
    font-size: var(--font-size-14);
    text-align: center;
    margin-top: var(--space-4);

    .product-set-title {
      &.skeleton {
        margin-left: auto;
        margin-right: auto;
        width: 200px;
        height: 1em;
      }
    }

    .product-set-price {
      margin-top: var(--space-2);
      .original-total {
        color: var(--grey-medium);
        text-decoration: line-through;
      }
       .final-total {
         margin-left: var(--space-2);
      }

      &.skeleton {
        width: 100px;
        margin-left: auto;
        margin-right: auto;
        height: 1em;
        .original-total,
        .final-total {
          color: transparent;
        }
      }
    }
  }

  .preserve-ratio-container .preserve-ratio-slot {
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    .image {
      width: 100%;
    }
  }
}
</style>
